import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Initials, StatusBadge } from "./CollaboratorBits";
import { Card } from "./ui/card";
import { Skeleton } from "./ui/skeleton";
import { UsersRound } from "lucide-react";

// « Équipe directe » : les personnes qui ont cette fiche pour responsable (rattachées à la fiche ou à son compte), dans le périmètre de
// l'appelant. Le serveur ne donne jamais le nombre de personnes qu'il ne voit pas. Rien n'est affiché tant qu'il n'y a personne.
export default function CollaboratorTeamCard({ fiche, serviceName }) {
  const [state, setState] = useState({ status: "loading", items: [] });
  const [withExited, setWithExited] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState((s) => (s.fiche === fiche.id ? { ...s, status: "loading" } : { status: "loading", items: [] })); // another person: never show the previous team
    api.get(`/collaborators/${fiche.id}/team`, { params: { include_exited: withExited || undefined } })
      .then(({ data }) => !cancelled && setState({ status: "ready", fiche: fiche.id, items: data.items, hasMore: data.has_more }))
      .catch(() => !cancelled && setState({ status: "error", items: [] }));
    return () => { cancelled = true; };
  }, [fiche.id, fiche.version, withExited]);

  if (state.status === "loading" && state.items.length === 0) return null; // no flash of an empty card for the many people who lead nobody
  if (state.status === "error") return null; // not allowed to read this relation, or a transient failure: the fiche itself stays usable
  if (state.status === "ready" && state.items.length === 0 && !withExited) return null;

  return (
    <Card className="p-5 space-y-3" data-testid="collab-team">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-display font-semibold text-sm text-slate-900">
          <UsersRound size={15} /> Équipe directe{state.status === "ready" && <span className="font-normal text-slate-500" data-testid="collab-team-count">({state.items.length}{state.hasMore ? "+" : ""})</span>}
        </div>
        <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer min-h-[32px]">
          <input type="checkbox" checked={withExited} onChange={(e) => setWithExited(e.target.checked)} data-testid="collab-team-exited" /> Avec les personnes sorties
        </label>
      </div>
      {state.status === "ready" && state.items.length > 0 && (
        <Link to="/collaborateurs" state={{ manager: { id: fiche.id, label: fiche.display_name } }} className="inline-block text-xs text-jamora hover:underline" data-testid="collab-team-directory">
          Voir cette équipe dans l'annuaire
        </Link>
      )}
      {state.status === "loading" && <Skeleton className="h-12 w-full" />}
      {state.status === "ready" && (
        <ul className="divide-y divide-slate-100" data-testid="collab-team-list">
          {state.items.map((member) => (
            <li key={member.id}>
              <Link to={`/collaborateurs/${member.id}`} data-testid={`collab-team-member-${member.id}`}
                className="flex items-center gap-3 py-2.5 min-h-[44px] hover:bg-slate-50 -mx-2 px-2 rounded-md">
                <Initials name={member.display_name} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-slate-900 break-words">{member.display_name}</span>
                  <span className="block text-xs text-slate-500 truncate">{[member.job_title, serviceName(member.service_id)].filter(Boolean).join(" · ")}</span>
                </span>
                <StatusBadge status={member.status} archived={member.archived} />
              </Link>
            </li>
          ))}
          {state.items.length === 0 && <li className="py-2 text-sm text-slate-500">Aucune personne sortie dans cette équipe.</li>}
        </ul>
      )}
    </Card>
  );
}
